import { useState, useCallback, useRef, useEffect } from 'react';
import { getApiClient } from 'utils/apiClient';
import { isPartnerTenant } from 'utils/tenantUtils';
import { INITIAL_LOADING_REFS } from 'utils/constants';
import { 
  normalizeMarketplaceProduct, 
  validateMarketplaceProduct,
  sortProductsForMarketplace 
} from 'utils/marketplaceMetadata';

/**
 * Custom hook for managing resource data loading
 * Handles loading of resources, tenants, cost pools, products, and prices
 */
export const useResourceData = ({
  resourceId,
  isOpen,
  mode,
  formData,
  tenants: existingTenants,
  isSelectedTenantPartner,
  setIsDevTenant,
  setFormData,
  setNextPlanLookupKey
}) => {
  // Internal state for tenants - will be synced with parent if needed
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [costPools, setCostPools] = useState([]);
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingCostPools, setLoadingCostPools] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);

  // Refs to track ongoing requests and prevent duplicates
  const loadingRefs = useRef(INITIAL_LOADING_REFS);
  const resourceDataPromiseRef = useRef(null);

  const loadResourceData = useCallback(async () => {
    if (!resourceId) {
      console.warn('Cannot load resource data: resourceId is null or undefined');
      return;
    }
    
    // Return existing promise if already loading (shared promise pattern)
    if (resourceDataPromiseRef.current) {
      return resourceDataPromiseRef.current;
    }
    
    // Create single shared promise
    resourceDataPromiseRef.current = (async () => {
      try {
        setLoading(true);
        setError(null);
        const apiClient = getApiClient();
        
        console.log('Loading resource data for ID:', resourceId);
        
        // Load resource details
        const response = await apiClient.get(`/v1/resources/${resourceId}`);
        
        console.log('API response:', response);
        
        if (response.success && response.data) {
          setResource(response.data);
          if (response.data?.billing_state?.pending_price_lookup_key) {
            setNextPlanLookupKey(response.data.billing_state.pending_price_lookup_key);
          }
          setFormData({
            name: response.data.name || '',
            type: response.data.type || 'bot',
            description: response.data.description || '',
            tenant_id: response.data.tenant_id || '',
            cost_pool_id: response.data.cost_pool_id || '',
            configuration: response.data.configuration || {}
          });
        } else {
          const raw = (response.error || 'Failed to load resource details').toLowerCase();
          const isNotFound = raw.includes('not found') || raw.includes('no item');
          const errorMsg = isNotFound ? 'Resource not found' : (response.error || 'Failed to load resource details');
          setError(errorMsg);
          console.error('API error:', errorMsg);
        }
      } catch (err) {
        const raw = (err.message || '').toLowerCase();
        const isNotFound = raw.includes('not found') || raw.includes('404') || raw.includes('400');
        const errorMsg = isNotFound ? 'Resource not found' : `Error loading resource details: ${err.message}`;
        setError(errorMsg);
        console.error(errorMsg, err);
      } finally {
        setLoading(false);
        resourceDataPromiseRef.current = null; // Clear promise reference
      }
    })();
    
    return resourceDataPromiseRef.current;
  }, [resourceId, setFormData, setNextPlanLookupKey]);

  const loadTenants = useCallback(async (forceRetry = false) => {
    // Prevent duplicate calls using ref
    if (loadingRefs.current.tenants && !forceRetry) {
      console.log('Tenants already loading, skipping duplicate call');
      return;
    }
    
    // Check if MSAL instance is available - tenants require authentication
    try {
      const apiClient = getApiClient();
      if (!apiClient.msalInstance) {
        console.log('⚠️ MSAL instance not initialized, skipping tenant load (user not authenticated)');
        setLoadingTenants(false);
        setTenants([]);
        return;
      }
    } catch (err) {
      console.log('⚠️ Cannot load tenants: authentication required');
      setLoadingTenants(false);
      setTenants([]);
      return;
    }
    
    try {
      loadingRefs.current.tenants = true;
      setLoadingTenants(true);
      const apiClient = getApiClient();
      const response = await apiClient.listWorkspaces();
      if (response.success) {
        setTenants(response.data || []);
      } else {
        console.error('Failed to load tenants:', response.error);
        setTenants([]);
      }
    } catch (err) {
      // Check if error is due to authentication
      if (err.message && (err.message.includes('MSAL instance not initialized') || err.message.includes('Authentication required'))) {
        console.log('⚠️ Tenants require authentication - user not logged in');
        setTenants([]);
      } else {
        console.error('Error loading tenants:', err);
        setTenants([]);
      }
    } finally {
      loadingRefs.current.tenants = false;
      setLoadingTenants(false);
    }
  }, []);

  const handleRetryTenants = () => {
    loadTenants(true);
  };

  const loadCostPools = useCallback(async (tenantId, includeAll = false) => {
    // Prevent duplicate calls using ref
    if (loadingRefs.current.costPools) {
      console.log('Cost pools already loading, skipping duplicate call');
      return;
    }
    
    // Check if MSAL instance is available - cost pools require authentication
    try {
      const apiClient = getApiClient();
      if (!apiClient.msalInstance) {
        console.log('⚠️ MSAL instance not initialized, skipping cost pool load (user not authenticated)');
        setLoadingCostPools(false);
        setCostPools([]);
        return;
      }
    } catch (err) {
      console.log('⚠️ Cannot load cost pools: authentication required');
      setLoadingCostPools(false);
      setCostPools([]);
      return;
    }
    
    try {
      loadingRefs.current.costPools = true;
      setLoadingCostPools(true);
      
      // Check if this is a dev tenant first
      const apiClient = getApiClient();
      if (tenantId) {
        const tenantResponse = await apiClient.getWorkspace(tenantId);
        
        if (tenantResponse.success && tenantResponse.data) {
          const isDevTenant = tenantResponse.data.is_dev_tenant || false;
          setIsDevTenant(isDevTenant);
          
          if (isDevTenant) {
            // Skip loading cost pools for dev tenants
            setCostPools([]);
            return;
          }
        }
      }
      
      // Load ALL cost pools (not filtered by tenant)
      // Don't pass workspaceId to get all cost pools across all tenants
      const response = await apiClient.listCostPools();
      if (response.success) {
        // Always filter to only show cost pools with valid payment methods
        const allCostPools = response.data || [];
        console.log('📦 Loaded all cost pools from API (not filtered by tenant):', {
          total: allCostPools.length,
          costPools: allCostPools.map(cp => ({
            id: cp.id,
            name: cp.name,
            tenant_id: cp.tenant_id,
            valid_payment_method: cp.valid_payment_method
          }))
        });
        
        // Only show cost pools with valid payment methods (same logic as Marketplace checkPaymentMethods)
        let validCostPools = allCostPools.filter(costPool => {
          if (costPool.valid_payment_method === true) return true;
          if (costPool.payment_methods && Array.isArray(costPool.payment_methods) && costPool.payment_methods.length > 0) {
            return costPool.payment_methods.some(pm =>
              pm && !pm.deleted && (pm.status === 'active' || pm.status === 'valid' || pm.status === undefined)
            );
          }
          return false;
        });
        // If API returned cost pools but none passed the filter (e.g. valid_payment_method not yet set), allow single cost pool so user can proceed
        if (validCostPools.length === 0 && allCostPools.length === 1) {
          validCostPools = allCostPools;
        }
        
        console.log('✅ Filtered cost pools (valid payment method only):', {
          total: validCostPools.length,
          costPools: validCostPools.map(cp => ({
            id: cp.id,
            name: cp.name,
            tenant_id: cp.tenant_id,
            valid_payment_method: cp.valid_payment_method
          }))
        });
        
        setCostPools(validCostPools);
      }
    } catch (err) {
      console.error('Error loading cost pools:', err);
    } finally {
      loadingRefs.current.costPools = false;
      setLoadingCostPools(false);
    }
  }, [setIsDevTenant]);

  const loadProducts = useCallback(async () => {
    // Prevent duplicate calls using ref
    if (loadingRefs.current.products) {
      console.log('Products already loading, skipping duplicate call');
      return;
    }
    
    // Set loading state immediately
    loadingRefs.current.products = true;
    setLoadingProducts(true);
    
    try {
      const apiClient = getApiClient();
      
      // Load products from public API - no authentication required
      // Prices are not included (loaded separately in configuration step)
      console.log('📦 Loading products from public API...');
      const response = await apiClient.getPublicProducts();
      
      console.log('📦 Products API response:', { 
        success: response.success, 
        hasData: !!response.data, 
        productsCount: response.data?.products?.length || 0,
        error: response.error,
        status: response.status
      });
      
      if (response.success) {
        let productsList = response.data?.products || [];
        
        // If a tenant is selected, check for connected resale resource and load its products
        if (formData.tenant_id) {
          try {
            const resourcesRes = await apiClient.listResources({ workspaceId: formData.tenant_id });
            const resaleResources = (resourcesRes.data || []).filter(r => r.type === 'resale');
            
            if (resaleResources.length > 0) {
              // Tenant has a resale resource - load its products
              const resaleResource = resaleResources[0]; // Use the first resale resource
              console.log('🏪 Found resale resource for tenant, loading its products:', resaleResource.id);
              
              // Load resale resource details to get enabled products
              const resaleDetailsRes = await apiClient.get(`/v1/resources/${resaleResource.id}`);
              if (resaleDetailsRes.success && resaleDetailsRes.data) {
                const resaleConfig = resaleDetailsRes.data.config?.sdk || {};
                const enabledProductIds = resaleConfig.enabled_product_ids || [];
                
                // Filter products to only show enabled ones from resale resource
                // Also mark them as coming from resale resource
                productsList = productsList.map(product => {
                  if (enabledProductIds.includes(product.id)) {
                    return {
                      ...product,
                      metadata: {
                        ...product.metadata,
                        from_resale_resource: true,
                        resale_resource_id: resaleResource.id,
                        resale_resource_name: resaleResource.name
                      }
                    };
                  }
                  return product;
                });
                
                console.log('🏪 Loaded products from resale resource:', {
                  resaleResourceId: resaleResource.id,
                  enabledProductIds,
                  totalProducts: productsList.length
                });
              }
            }
          } catch (resaleErr) {
            console.warn('Error loading resale resource products:', resaleErr);
            // Continue with standard products if resale loading fails
          }
        }
        
        // Normalize and validate products
        const normalizedProducts = [];
        const productErrors = [];
        
        productsList.forEach(product => {
          // Skip archived/inactive products in Stripe (active: false means archived)
          if (product.active === false) {
            return;
          }
          
          // Filter by status metadata first
          const status = product.metadata?.status;
          if (status && status !== 'public' && status !== 'public_development') {
            // Skip development and legacy products
            return;
          }
          
          // Normalize marketplace metadata
          const normalized = normalizeMarketplaceProduct(product);
          
          // Validate required metadata for Besper products (only for active products)
          const validation = validateMarketplaceProduct(product);
          if (!validation.isValid) {
            // Log errors but don't block the product (for now)
            console.warn(`Product ${product.name || product.id} has metadata issues:`, validation.errors);
            productErrors.push({
              productId: product.id,
              productName: product.name || 'Unnamed Product',
              errors: validation.errors
            });
          }
          
          // Include all products (besper and third_party) so marketplace shows all providers
          normalizedProducts.push(normalized);
        });
        
        // Sort products for marketplace display
        const sortedProducts = sortProductsForMarketplace(normalizedProducts);
        
        console.log('📦 Final products list:', sortedProducts);
        if (productErrors.length > 0) {
          console.warn('⚠️ Products with metadata issues:', productErrors);
        }
        
        setProducts(sortedProducts);
      } else {
        console.error('Failed to load products:', response.error);
        setProducts([]); // Set empty array on failure
      }
    } catch (err) {
      // Public API should work without authentication, but handle errors gracefully
      console.error('Error loading products:', err);
      setProducts([]); // Set empty array on error
    } finally {
      // Always reset loading state, even if there was an error
      loadingRefs.current.products = false;
      setLoadingProducts(false);
    }
  }, [formData.tenant_id, tenants, existingTenants]);

  const loadPrices = useCallback(async (productId, explicitTenantId = null) => {
    // Prevent duplicate calls using ref
    if (loadingRefs.current.prices) {
      console.log('Prices already loading, skipping duplicate call');
      return;
    }
    
    if (!productId) {
      console.warn('Cannot load prices: productId is required');
      return;
    }
    
    try {
      loadingRefs.current.prices = true;
      setLoadingPrices(true);
      
      const apiClient = getApiClient();
      
      // Use explicit tenantId if provided, otherwise fall back to formData (for backward compatibility)
      // This fixes the race condition where formData.tenant_id might be stale
      const selectedTenantId = explicitTenantId || formData?.tenant_id;
      
      // Determine tenant type from the explicit tenant if provided
      const tenantForType = explicitTenantId 
        ? tenants.find(t => t.id === explicitTenantId)
        : (selectedTenantId ? tenants.find(t => t.id === selectedTenantId) : null);
      const isPartner = tenantForType ? isPartnerTenant(tenantForType) : isSelectedTenantPartner;
      
      console.log('💰 Loading prices:', {
        productId,
        selectedTenantId,
        explicitTenantId,
        isPartner,
        tenant: tenantForType
      });
      
      // If tenant is selected, use effective prices API (returns ALL price tiers)
      if (selectedTenantId) {
        try {
          const effectiveResponse = await apiClient.get(
            `/v1/pricing/effective?tenant_id=${selectedTenantId}&product_id=${productId}`
          );
          
          if (effectiveResponse && effectiveResponse.success) {
            const allPrices = effectiveResponse.prices || {};
            const isResaleProduct = !!effectiveResponse.is_resale_product;
            if (isResaleProduct) {
              console.log('📦 Effective prices response (resale product):', allPrices);
            } else {
              console.log('📦 Effective prices response (all tiers):', allPrices);
            }
            
            // Convert new format { period: { tier: priceData } } to flat array
            // The new API returns ALL price tiers (standard, partner_wholesale, development, partner_base, managed)
            // Frontend filtering (in BasicsTab.js) will show appropriate prices based on tenant type
            const pricesList = [];
            
            for (const [period, tierPrices] of Object.entries(allPrices)) {
              const normalizedPeriod = period === 'month' ? 'monthly' : period === 'year' ? 'yearly' : period;
              
              for (const [mapKey, priceData] of Object.entries(tierPrices)) {
                if (!priceData || !priceData.price_id) continue;
                const logicalTier = priceData.tier ?? mapKey;
                const apiMeta = priceData.metadata || {};
                const priceObj = {
                  id: priceData.price_id,
                  lookup_key: priceData.lookup_key || priceData.price_id,
                  product_id: productId,
                  active: true,
                  interval: normalizedPeriod,
                  interval_count: 1,
                  recurring: { interval: period === 'year' ? 'year' : 'month' },
                  unit_amount: priceData.amount,
                  currency: priceData.currency || 'eur',
                  metadata: {
                    tier: logicalTier,
                    original_amount: priceData.original_amount,
                    discount_percentage: priceData.discount_percentage,
                    is_resale_product: isResaleProduct,
                    description: apiMeta.description,
                    usage_limits: apiMeta.usage_limits,
                    nickname: apiMeta.nickname
                  }
                };
                
                console.log(`  Adding price: ${logicalTier} / ${period} - ${priceData.lookup_key} - ${priceData.amount}`);
                pricesList.push(priceObj);
              }
            }
            
            // If resale product, also update formData.configuration.is_resale_product (only when not already set to avoid loop)
            if (isResaleProduct && setFormData) {
              setFormData(prev => {
                if (prev.configuration?.is_resale_product === true) return prev;
                return {
                  ...prev,
                  configuration: {
                    ...prev.configuration,
                    is_resale_product: true
                  }
                };
              });
            }
            
            console.log(`✅ Converted ${pricesList.length} prices from effective API (server-side filtered)`);
            setPrices(pricesList);
            return;
          } else {
            console.warn('Effective prices API returned success: false:', effectiveResponse?.error);
          }
        } catch (effectiveErr) {
          console.warn('Effective prices API failed:', effectiveErr);
        }
        setPrices([]);
        return;
      }

      if (!selectedTenantId) {
        console.warn('⚠️ Cannot load prices: tenant must be selected first');
        setPrices([]);
        return;
      }
      
      // If effective prices failed, fall back to regular prices; pass tenant_id so backend filters server-side
      const response = await apiClient.get(`/v1/payments/prices?product_id=${productId}&tenant_id=${selectedTenantId}`);
      if (response.success) {
        let pricesList = response.data?.prices || [];
        
        console.log('📦 Raw prices from API (fallback):', pricesList.length);
        
        // Filter prices to only show active ones
        pricesList = pricesList.filter(price => price.active === true);
        
        console.log('✅ Active prices (fallback):', pricesList.length);
        
        // Server-side filtering should handle tier filtering, but if we're using fallback,
        // we still need basic filtering (prices are already filtered server-side in effective API)
        console.log('✅ Final prices to set (fallback):', pricesList.length, pricesList.map(p => p.lookup_key));
        setPrices(pricesList);
      } else {
        console.error('❌ Failed to load prices from API:', response);
        setPrices([]);
      }
    } catch (err) {
      console.error('Error loading prices:', err);
    } finally {
      loadingRefs.current.prices = false;
      setLoadingPrices(false);
    }
  // Deps: use formData?.tenant_id (not formData) so updating only configuration.is_resale_product does not recreate loadPrices and retrigger price-load effects
  }, [isSelectedTenantPartner, formData?.tenant_id, tenants]);

  // Load resource data when modal opens
  useEffect(() => {
    if (isOpen && resourceId) {
      loadResourceData();
    }
  }, [isOpen, resourceId, loadResourceData]);

  // Track if we've loaded products initially to prevent infinite loops
  const productsLoadedRef = useRef(false);
  const lastTenantIdRef = useRef(null);
  const loadProductsStableRef = useRef(null);

  // Create a stable reference to loadProducts that doesn't change
  useEffect(() => {
    loadProductsStableRef.current = loadProducts;
  }, [loadProducts]);

  // Load dropdown data when modal opens in create mode
  useEffect(() => {
    console.log('🔄 useResourceData useEffect triggered:', { isOpen, mode, hasLoadProducts: !!loadProductsStableRef.current, productsLoaded: productsLoadedRef.current });
    
    if (isOpen && mode === 'create') {
      loadTenants();
      // Load products initially (only once)
      if (!productsLoadedRef.current && loadProductsStableRef.current) {
        console.log('📦 Calling loadProducts from useEffect');
        loadProductsStableRef.current();
        productsLoadedRef.current = true;
      } else {
        console.log('⚠️ Skipping product load:', { 
          productsLoaded: productsLoadedRef.current, 
          hasLoadProducts: !!loadProductsStableRef.current 
        });
      }
    } else {
      // Reset when modal closes
      productsLoadedRef.current = false;
      lastTenantIdRef.current = null;
    }
  }, [isOpen, mode, loadTenants]);

  // Reload products when tenant_id changes (for partner check)
  useEffect(() => {
    if (isOpen && mode === 'create' && formData.tenant_id && formData.tenant_id !== lastTenantIdRef.current) {
      lastTenantIdRef.current = formData.tenant_id;
      // Only reload if we have tenants loaded and products have been loaded at least once
      if (tenants.length > 0 && productsLoadedRef.current && loadProductsStableRef.current) {
        loadProductsStableRef.current();
      }
    }
  }, [isOpen, mode, formData.tenant_id, tenants.length]);

  // Load all cost pools when modal opens in create mode
  // Now we load ALL cost pools (not filtered by tenant), only showing ones with valid payment methods
  useEffect(() => {
    if (isOpen && mode === 'create' && !loadingRefs.current.costPools) {
      // Load all cost pools immediately when modal opens
      // tenantId is optional - only used to check if it's a dev tenant
      const tenantIdForCheck = formData.tenant_id || null;
      loadCostPools(tenantIdForCheck, false);
    }
  }, [isOpen, mode, loadCostPools]);

  // Load prices for plan-change UI in view mode (Stripe resources only)
  useEffect(() => {
    if (mode !== 'view' || !resource) return;
    if (resource?.is_dev_resource) return;
    if (!resource?.product_id || resource.product_id === 'free') return;
    // Pass resource tenant_id explicitly to ensure correct pricing context
    const tenantId = resource.tenant_id || formData?.tenant_id || null;
    loadPrices(resource.product_id, tenantId);
  }, [mode, resource, formData?.tenant_id, loadPrices]);

  return {
    // Data
    resource,
    tenants,
    costPools,
    products,
    prices,
    // Loading states
    loading,
    error,
    loadingTenants,
    loadingCostPools,
    loadingProducts,
    loadingPrices,
    // Actions
    loadResourceData,
    loadTenants,
    handleRetryTenants,
    loadCostPools,
    loadProducts,
    loadPrices,
    // Setters (for external updates)
    setResource,
    setError,
    setLoading
  };
};

